import type { BrandingOverrideValue } from '@/hooks/use-branding'

import { EDUNODES_COMMUNITY_FAVICON_URL, EDUNODES_COMMUNITY_LOGO_URL } from '@/constant/edunodes'

import type { FooterConfig, HeaderConfig } from '@/types/header-footer-config'
import type { MainNavItem } from '@/types/nav.type'

/* ─── Navigation ─── */

export const NON_FSDS_NAV_ITEMS: MainNavItem[] = [
  { title: 'Lộ trình', href: '/learning-path', items: [] },
  { title: 'Blog', href: '/blogs', items: [] },
  { title: 'Webinars', href: '/webinars', items: [] },
  { title: 'Online Tests', href: '/online-tests', items: [] },
  { title: 'Bài mẫu', href: '/example-practices', items: [] },
  { title: 'Cộng đồng', href: '/forum', items: [] },
  { title: 'Hall of fame', href: '/hall-of-fame', items: [] },
]

/** Puck template placeholder; treated as unset so tenant media logo applies. */
export const LEGACY_NON_FSDS_LOGO_URL = '/svg/non-fsds-icon.svg'

export const NON_FSDS_DEFAULT_LOGO_URL = EDUNODES_COMMUNITY_LOGO_URL

export const NON_FSDS_DEFAULT_FAVICON_URL = EDUNODES_COMMUNITY_FAVICON_URL

const PUCK_LOGO_PLACEHOLDER_URLS = new Set([LEGACY_NON_FSDS_LOGO_URL, NON_FSDS_DEFAULT_LOGO_URL])

/* ─── Header / Footer configs ─── */

export const NON_FSDS_HEADER_CONFIG: HeaderConfig = {
  layoutPreset: 'logo-left',
  sticky: true,
  showBorder: false,
  containerWidth: 'full',
  spacing: 'relaxed',
  navItems: NON_FSDS_NAV_ITEMS,
  mobileNavItems: NON_FSDS_NAV_ITEMS,
}

/** Puck template seed props — nav matches live tenant header via getNonFsdsHeaderConfig. */
export const NON_FSDS_TEMPLATE_PUCK_HEADER_DEFAULT_PROPS = {
  visible: true,
  fallbackLogoUrl: NON_FSDS_DEFAULT_LOGO_URL,
  brandHref: '/',
  brandName: '',
  headerConfig: NON_FSDS_HEADER_CONFIG,
} as const

export const NON_FSDS_FOOTER_CONFIG: FooterConfig = {
  layoutPreset: 'centered',
  showLogo: true,
  showLinkGroups: false,
  showSocialLinks: true,
  showCopyright: true,
  showBorder: false,
  containerWidth: 'wide',
  spacing: 'relaxed',
}

export const NON_FSDS_TEMPLATE_PUCK_FOOTER_DEFAULT_PROPS = {
  visible: true,
  fallbackLogoUrl: NON_FSDS_DEFAULT_LOGO_URL,
  brandHref: '/',
  brandName: '',
  footerConfig: NON_FSDS_FOOTER_CONFIG,
} as const

/* ─── Branding overrides ─── */

/** Empty override — site logo comes from admin/media-defaults (tenant config). */
export const NON_FSDS_HEADER_BRANDING_OVERRIDE: BrandingOverrideValue = {
  fallbackLogoUrl: '',
}

export const NON_FSDS_FOOTER_BRANDING_OVERRIDE: BrandingOverrideValue = {
  fallbackLogoUrl: '',
}

/*
 * Theme CSS variables live in globals.css under `.theme-non-fsds`.
 * RootContainer applies that class automatically for non-FSDS domains,
 * so no JS-side theme object is needed.
 */

function normalizePublishedBrandingOverride(
  override: BrandingOverrideValue
): BrandingOverrideValue {
  const logo = override.fallbackLogoUrl?.trim() ?? ''
  if (!logo || PUCK_LOGO_PLACEHOLDER_URLS.has(logo)) {
    return { ...override, fallbackLogoUrl: '' }
  }
  return override
}

/* ─── Helper: merge published overrides on top of non-FSDS defaults ─── */

export function getNonFsdsHeaderBrandingOverride(publishedOverride?: BrandingOverrideValue): BrandingOverrideValue {
  return normalizePublishedBrandingOverride({
    ...NON_FSDS_HEADER_BRANDING_OVERRIDE,
    ...(publishedOverride ?? {}),
  })
}

export function getNonFsdsFooterBrandingOverride(publishedOverride?: BrandingOverrideValue): BrandingOverrideValue {
  return normalizePublishedBrandingOverride({
    ...NON_FSDS_FOOTER_BRANDING_OVERRIDE,
    ...(publishedOverride ?? {}),
  })
}

export function getNonFsdsHeaderConfig(publishedConfig?: HeaderConfig): HeaderConfig {
  if (!publishedConfig) {
    return { ...NON_FSDS_HEADER_CONFIG }
  }

  // For v2 templates with published navigation, honor the full config
  const hasPublishedNav =
    Array.isArray(publishedConfig.navItems) && publishedConfig.navItems.length > 0

  if (hasPublishedNav) {
    return { ...NON_FSDS_HEADER_CONFIG, ...publishedConfig }
  }

  // Legacy configs: strip empty/default navItems to prevent clearing the fallback nav
  const { navItems: _nav, mobileNavItems: _mobileNav, ...safeOverrides } = publishedConfig
  return { ...NON_FSDS_HEADER_CONFIG, ...safeOverrides }
}

export function getNonFsdsFooterConfig(publishedConfig?: FooterConfig): FooterConfig {
  return { ...NON_FSDS_FOOTER_CONFIG, ...(publishedConfig ?? {}) }
}
